import { lazy, Suspense } from 'react';
import { Navigate, useRoutes, useParams, useLocation } from 'react-router-dom';
// layouts (eager — small, shared across nearly every route)
import DashboardLayout from './layouts/dashboard';
import LogoOnlyLayout from './layouts/LogoOnlyLayout';
// loading fallback shown while a route's chunk is fetching
import LoadingScreen from './components/LoadingScreen';
import GameRouteWrapper from './components/GameRouteWrapper';

// Page modules are lazy-loaded so each route ships as its own webpack chunk.
// Cuts the initial JS payload from ~2 MB to the layout + the landing route's
// chunk; subsequent routes fetch on demand and are cached by the immutable
// hashed-asset Cache-Control headers.
const User = lazy(() => import('./pages/User'));
const People = lazy(() => import('./pages/People'));
const Messages = lazy(() => import('./pages/Messages'));
const MessageThread = lazy(() => import('./pages/MessageThread'));
const Login = lazy(() => import('./pages/Login'));
const NotFound = lazy(() => import('./pages/Page404'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));

const DashboardApp = lazy(() => import('./pages/DashboardApp'));
const VotePage = lazy(() => import('./pages/VotePage'));
const CreatorStudio = lazy(() => import('./pages/CreatorStudio'));
const Studio = lazy(() => import('./pages/Studio'));
const StudioPostDetail = lazy(() => import('./pages/StudioPostDetail'));
const Challenge = lazy(() => import('./pages/Challenge'));
const ChallengeHub = lazy(() => import('./pages/ChallengeHub'));
const ChallengeContrarian = lazy(() => import('./pages/ChallengeContrarian'));
const ChallengeStreak = lazy(() => import('./pages/ChallengeStreak'));
const ChallengeBlindBet = lazy(() => import('./pages/ChallengeBlindBet'));
const ChallengeDaily = lazy(() => import('./pages/ChallengeDaily'));
const ChallengeCategory = lazy(() => import('./pages/ChallengeCategory'));
const ChallengeVersus = lazy(() => import('./pages/ChallengeVersus'));
const ChallengeAgree = lazy(() => import('./pages/ChallengeAgree'));
const ChallengeMemory = lazy(() => import('./pages/ChallengeMemory'));
const ChallengeStreamer = lazy(() => import('./pages/ChallengeStreamer'));
const ChallengeDare = lazy(() => import('./pages/ChallengeDare'));
const MindMeldHub = lazy(() => import('./pages/MindMeldHub'));
const MindMeldRoom = lazy(() => import('./pages/MindMeldRoom'));
const Invite = lazy(() => import('./pages/Invite'));
const Profile = lazy(() => import('./pages/Profile'));
const Bookmarks = lazy(() => import('./pages/Bookmarks'));
const Notifications = lazy(() => import('./pages/Notifications'));
const Legal = lazy(() => import('./pages/Legal'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const Claim = lazy(() => import('./pages/Claim'));
const Settings = lazy(() => import('./pages/Settings'));
const OverlayView = lazy(() => import('./pages/OverlayView'));
const ComparisonPage = lazy(() => import('./pages/ComparisonPage'));
const BracketPage = lazy(() => import('./pages/BracketPage'));
const ReferralDashboard = lazy(() => import('./pages/ReferralDashboard'));

// Wraps every route element in <Suspense> so the LoadingScreen appears while
// the chunk downloads. ErrorBoundary in App.js catches chunk-load failures
// (network drop mid-session) and surfaces a "refresh" CTA which fetches the
// updated asset hashes after a deploy.
const lazyRoute = (Element) => (
  <Suspense fallback={<LoadingScreen />}>
    <Element />
  </Suspense>
);

// Short URL redirect: /q/:id → /v/:id (canonical vote page).
// Kept forever as a legacy alias — production share links use /q/:id.
function QuestionRedirect() {
  const { id, slug } = useParams();
  const location = useLocation();
  const dest = slug ? `/v/${id}/${slug}` : `/v/${id}`;
  return <Navigate to={`${dest}${location.search}`} replace />;
}

// Capture-anything redirect for legacy /dashboard/challenge/* deep links that
// don't have a 1-to-1 mapping handled inline below (defensive — should be rare
// since each known sub-route gets its own redirect entry).
function ChallengeRedirect() {
  const { '*': rest } = useParams();
  const location = useLocation();
  const tail = rest ? `/${rest}` : '';
  return <Navigate to={`/play${tail}${location.search}`} replace />;
}

// Param-bearing legacy redirects. React Router's <Navigate> doesn't support
// "use the matched param in the destination" declaratively, so each one needs
// its own thin component. Declared above Router() so eslint's
// no-use-before-define is happy without relying on hoisting.

function VoteRedirect() {
  const { id } = useParams();
  const location = useLocation();
  return <Navigate to={`/v/${id}${location.search}`} replace />;
}

function ProfileRedirect() {
  const { username } = useParams();
  const location = useLocation();
  return <Navigate to={`/u/${username}${location.search}`} replace />;
}

function CategoryRedirect() {
  const { slug } = useParams();
  const location = useLocation();
  return <Navigate to={`/play/category/${slug}${location.search}`} replace />;
}

function VersusRedirect() {
  const { code } = useParams();
  const location = useLocation();
  return <Navigate to={`/play/versus/${code}${location.search}`} replace />;
}

function AgreeRedirect() {
  const { code } = useParams();
  const location = useLocation();
  return <Navigate to={`/play/agree/${code}${location.search}`} replace />;
}

function MemoryRedirect() {
  const { code } = useParams();
  const location = useLocation();
  return <Navigate to={`/play/memory/${code}${location.search}`} replace />;
}

function MindMeldRedirect() {
  const { code } = useParams();
  const location = useLocation();
  return <Navigate to={`/play/mindmeld/${code}${location.search}`} replace />;
}

// ----------------------------------------------------------------------

export default function Router() {
  return useRoutes([
    // Canonical SPA routes (post URL-hygiene refactor). Twitter/X-style.
    {
      path: '/',
      element: <DashboardLayout />,
      children: [
        { index: true, element: lazyRoute(DashboardApp) },
        { path: 'v/:id', element: lazyRoute(VotePage) },
        // Slugged canonical (Stack Overflow / Reddit style). The slug is
        // cosmetic — VotePage only reads :id. Backend share.ts emits this
        // shape in og:url + sitemap so search-result clicks land here, not
        // the bare /v/:id.
        { path: 'v/:id/:slug', element: lazyRoute(VotePage) },
        // `/new` stays the compose deep-link (the 3-step create flow).
        { path: 'new', element: lazyRoute(CreatorStudio) },
        // Creator Studio shell — Overview / Content over one list payload,
        // plus the per-post analytics + lifecycle surface.
        { path: 'studio', element: lazyRoute(Studio) },
        { path: 'studio/content', element: lazyRoute(Studio) },
        { path: 'studio/post/:id', element: lazyRoute(StudioPostDetail) },
        { path: 'questions', element: <Navigate to="/play/category" replace /> },
        { path: 'questions/:slug', element: <CategoryRedirect /> },
        { path: 'play', element: <GameRouteWrapper>{lazyRoute(ChallengeHub)}</GameRouteWrapper> },
        { path: 'play/rapid', element: <GameRouteWrapper>{lazyRoute(Challenge)}</GameRouteWrapper> },
        { path: 'play/contrarian', element: <GameRouteWrapper>{lazyRoute(ChallengeContrarian)}</GameRouteWrapper> },
        { path: 'play/streak', element: <GameRouteWrapper>{lazyRoute(ChallengeStreak)}</GameRouteWrapper> },
        { path: 'play/blindbet', element: <GameRouteWrapper>{lazyRoute(ChallengeBlindBet)}</GameRouteWrapper> },
        {path: 'play/daily', element: <GameRouteWrapper>{lazyRoute(ChallengeDaily)}</GameRouteWrapper> },
        { path: 'play/category', element: <GameRouteWrapper>{lazyRoute(ChallengeCategory)}</GameRouteWrapper> },
        { path: 'play/category/:slug', element: <GameRouteWrapper>{lazyRoute(ChallengeCategory)}</GameRouteWrapper> },
        { path: 'play/streamer', element: <GameRouteWrapper>{lazyRoute(ChallengeStreamer)}</GameRouteWrapper> },
        { path: 'play/streamer/:code', element: <GameRouteWrapper>{lazyRoute(ChallengeStreamer)}</GameRouteWrapper> },
        { path: 'play/dare', element: <GameRouteWrapper>{lazyRoute(ChallengeDare)}</GameRouteWrapper> },
        { path: 'play/dare/:code', element: <GameRouteWrapper>{lazyRoute(ChallengeDare)}</GameRouteWrapper> },
        { path: 'play/versus', element: <GameRouteWrapper>{lazyRoute(ChallengeVersus)}</GameRouteWrapper> },
        { path: 'play/versus/:code', element: <GameRouteWrapper>{lazyRoute(ChallengeVersus)}</GameRouteWrapper> },
        { path: 'play/agree', element: <GameRouteWrapper>{lazyRoute(ChallengeAgree)}</GameRouteWrapper> },
        { path: 'play/agree/:code', element: <GameRouteWrapper>{lazyRoute(ChallengeAgree)}</GameRouteWrapper> },
        { path: 'play/memory', element: <GameRouteWrapper>{lazyRoute(ChallengeMemory)}</GameRouteWrapper> },
        { path: 'play/memory/:code', element: <GameRouteWrapper>{lazyRoute(ChallengeMemory)}</GameRouteWrapper> },
        { path: 'play/mindmeld', element: <GameRouteWrapper>{lazyRoute(MindMeldHub)}</GameRouteWrapper> },
        { path: 'play/mindmeld/:code', element: <GameRouteWrapper>{lazyRoute(MindMeldRoom)}</GameRouteWrapper> },
        { path: 'saved', element: lazyRoute(Bookmarks) },
        { path: 'discover', element: lazyRoute(User) },
        { path: 'u/:username', element: lazyRoute(Profile) },
        // Social layer. `/people` is the viewer's own hub; the `/u/:username/`
        // forms are the public follower/following lists for any profile.
        { path: 'people', element: lazyRoute(People) },
        { path: 'people/requests', element: lazyRoute(People) },
        { path: 'u/:username/followers', element: lazyRoute(People) },
        { path: 'u/:username/following', element: lazyRoute(People) },
        // Direct messages. STILL two routes, but they now render a two-pane
        // layout at `lg`: /messages is the list plus an empty right panel,
        // /messages/:id is the list plus the thread. The earlier note here
        // rejected a desktop split as too costly to maintain against the
        // layout chrome — that turned out not to apply, because /messages
        // matches no DashboardLayout special case and the split is purely
        // presentational inside the two pages. The route pair is unchanged, so
        // deep links, bell targets and the phone flow all behave as before.
        // No /dashboard alias — these paths are new, so there is no install
        // base to preserve.
        { path: 'messages', element: lazyRoute(Messages) },
        { path: 'messages/:id', element: lazyRoute(MessageThread) },
        { path: 'notifications', element: lazyRoute(Notifications) },
        { path: 'claim', element: lazyRoute(Claim) },
        { path: 'settings', element: lazyRoute(Settings) },
        // Admin dashboard — component itself gates on owner check and
        // redirects non-owners to /404. The backend rejects non-owners
        // at the API layer regardless.
        { path: 'admin', element: lazyRoute(AdminDashboard) },
        { path: 'compare/:slug', element: lazyRoute(ComparisonPage) },
        { path: 'bracket/:id', element: lazyRoute(BracketPage) },
        { path: 'referrals', element: lazyRoute(ReferralDashboard) },
      ],
    },

    // Legacy /dashboard/* → new canonical (kept forever for install-base
    // preservation: existing bookmarks, Capacitor wrapper deep links, and
    // any out-of-band shared URLs continue to resolve via 301-equivalent
    // SPA redirects). Backend SSR redirects live in routes/share.ts so
    // crawlers (Twitter/Discord/Slack unfurls) follow Location headers.
    {
      path: '/dashboard',
      element: <DashboardLayout />,
      children: [
        { index: true, element: <Navigate to="/" replace /> },
        { path: 'app', element: <Navigate to="/" replace /> },
        { path: 'vote/:id', element: <VoteRedirect /> },
        { path: 'create', element: <Navigate to="/new" replace /> },
        { path: 'challenge', element: <Navigate to="/play" replace /> },
        // Rapid-fire mode renamed: /dashboard/challenge/play → /play/rapid.
        { path: 'challenge/play', element: <Navigate to="/play/rapid" replace /> },
        { path: 'challenge/contrarian', element: <Navigate to="/play/contrarian" replace /> },
        { path: 'challenge/streak', element: <Navigate to="/play/streak" replace /> },
        { path: 'challenge/blindbet', element: <Navigate to="/play/blindbet" replace /> },
        { path: 'challenge/daily', element: <Navigate to="/play/daily" replace /> },
        { path: 'challenge/category', element: <Navigate to="/play/category" replace /> },
        { path: 'challenge/category/:slug', element: <CategoryRedirect /> },
        { path: 'challenge/versus', element: <Navigate to="/play/versus" replace /> },
        { path: 'challenge/versus/:code', element: <VersusRedirect /> },
        { path: 'challenge/agree', element: <Navigate to="/play/agree" replace /> },
        { path: 'challenge/agree/:code', element: <AgreeRedirect /> },
        { path: 'challenge/memory', element: <Navigate to="/play/memory" replace /> },
        { path: 'challenge/memory/:code', element: <MemoryRedirect /> },
        { path: 'challenge/mindmeld', element: <Navigate to="/play/mindmeld" replace /> },
        { path: 'challenge/mindmeld/:code', element: <MindMeldRedirect /> },
        // Defensive catch-all for any future challenge sub-route.
        { path: 'challenge/*', element: <ChallengeRedirect /> },
        // iv-γ: SPA-owned invite landing page. Backend's /i/:type/:code SSR
        // shim redirects humans to this path; Invite.js gates on userInfo
        // (anon-minted by App.js), then auto-joins and forwards to /play/*.
        { path: 'i/:type/:code', element: <GameRouteWrapper>{lazyRoute(Invite)}</GameRouteWrapper> },
        { path: 'saved', element: <Navigate to="/saved" replace /> },
        // Renamed: /dashboard/user (directory) → /discover.
        { path: 'user', element: <Navigate to="/discover" replace /> },
        { path: 'profile/:username', element: <ProfileRedirect /> },
        { path: 'notifications', element: <Navigate to="/notifications" replace /> },
        { path: 'claim', element: <Navigate to="/claim" replace /> },
        { path: 'settings', element: <Navigate to="/settings" replace /> },
        { path: 'admin', element: <Navigate to="/admin" replace /> },
      ],
    },

    // Streamer Overlay Mode route
    { path: 'overlay/:roomCode', element: lazyRoute(OverlayView) },

    // Short URL alias — kept forever, redirects to /v/:id canonical.
    { path: '/q/:id', element: <QuestionRedirect /> },
    { path: '/q/:id/:slug', element: <QuestionRedirect /> },

    {
      path: '/',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'login', element: lazyRoute(Login) },
        { path: 'register', element: lazyRoute(Register) },
        { path: 'forgot-password', element: lazyRoute(ForgotPassword) },
        { path: 'reset-password', element: lazyRoute(ResetPassword) },
        { path: 'legal/:page', element: lazyRoute(Legal) },
        { path: '404', element: lazyRoute(NotFound) },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}
